// Stateful walk over a puzzle's solution DAG (spec §7.2, §13-7, §13-8).
//
// Path-dependent word-bar classification. Replaces the memoryless per-slot
// union lookup (slotStates.ts:classifySlot) with a *stateful* walk: a move is
// 'correct-canonical' (blue) only when it follows an edge that keeps a win
// reachable from the current DAG node. The same right letter played via a move
// that strands the player goes 'correct-alt' (orange).
//
// This is a pure, DOM-free, framework-free function — the line-for-line
// behavioral mirror of the iOS reference SolutionGraphWalker.swift
// (device-verified 2026-06-07). The walk holds NO persisted state: the pointer
// (current node) is DERIVED — recomputed by a full replay from `root` on every
// call. Nothing here is written into any snapshot or persisted record; the only
// persisted input is the provenance the game state already keeps
// (state.exitedProvenance) and the exited letters (state.exitedLetters).
//
// Off-graph freeze (§7.2 step 3): the first filled slot whose move has no
// matching outgoing edge sets `offGraph` and freezes the pointer. Every later
// filled slot with the correct letter is then orange — the walk CANNOT resume
// after a gap, because a single missing edge means no winning continuation
// exists from that point on.
//
// Wrong-letter off-graph (§13-7, curator-locked 2026-06-07): a wrong letter
// also takes the walk off-graph, exactly like an unmatched edge. The bar can
// only grow or be undone, so once a wrong letter sits in it no future bar state
// can equal the target — no winning continuation exists. Without this, a LATER
// move could spuriously match a stale edge from the frozen pointer and show a
// false blue (the exact bug class this walk exists to kill).

import type { EncodedCell } from './state'
import { BOARD_SIZE } from './state'
import type { FilledSlotState } from './slotStates'

// On-wire / parsed shape of the per-puzzle solution DAG (spec §10). Direction
// ints: UP=0 DOWN=1 LEFT=2 RIGHT=3. `source_tile` is the FLICK-TIME [row, col].
// A node may have MULTIPLE outgoing edges sharing the same `from` (the
// energetic root has two), so adjacency MUST be from → edge[], never a
// one-edge-per-from map.
export interface SolutionGraphNode {
  readonly id: number
  readonly win: boolean
}

export interface SolutionGraphEdge {
  readonly from: number
  // `sourceCell` is the EncodedCell (row * BOARD_SIZE + col) of the flick-time
  // cell, converted from the graph's [row, col] source_tile at parse time so it
  // compares directly against state.exitedProvenance[i].sourceCell.
  readonly sourceCell: EncodedCell
  readonly direction: number
  readonly to: number
}

export interface SolutionGraph {
  readonly root: number
  readonly nodes: readonly SolutionGraphNode[]
  readonly edges: readonly SolutionGraphEdge[]
}

// One step of the player's history: the cell flicked from and the direction.
// Length-parallel to the exited letters fed in alongside it. Mirrors the
// runtime ExitProvenance shape (state.ts) field-for-field, so callers pass
// state.exitedProvenance directly.
export interface SolutionGraphMove {
  readonly sourceCell: EncodedCell
  readonly direction: number
}

export interface SolutionGraphWalkResult {
  // Per-slot classification, length == min(exitedLetters, provenance) walked.
  // Indexed by slot. Never contains 'empty' — only filled slots are walked.
  readonly slotStates: readonly FilledSlotState[]
  // True once the walk has left the DAG (an unmatched edge OR a wrong letter).
  readonly offGraph: boolean
  // True when the pointer rests on a `win` node (full canonical solve).
  readonly onWinNode: boolean
}

// Convert a graph's [row, col] source_tile to the EncodedCell frame the client
// provenance uses (state.ts encode: row * BOARD_SIZE + col). Exported for the
// corpus parser, which validates + encodes at parse time.
export function encodeSourceTile(row: number, col: number): EncodedCell {
  return row * BOARD_SIZE + col
}

// On-wire solution-DAG shape (spec §10). The wire uses the schema's snake_case
// `source_tile` / `from` / `to` / `win` keys verbatim — `source_tile` is a
// flick-time [row, col] pair, encoded to the runtime EncodedCell frame at parse
// time. Both the daily corpus (corpus.ts) and the practice corpus (practice.ts)
// carry this same shape; parsing/validation lives here so both bundles share a
// single, audited code path.
export interface WireSolutionGraphNode {
  readonly id: number
  readonly win: boolean
}
export interface WireSolutionGraphEdge {
  readonly from: number
  readonly source_tile: readonly [number, number]
  readonly direction: number
  readonly to: number
}
export interface WireSolutionGraph {
  readonly root: number
  readonly nodes: readonly WireSolutionGraphNode[]
  readonly edges: readonly WireSolutionGraphEdge[]
}

// Validate + parse an on-wire solution_graph into the runtime SolutionGraph,
// encoding each edge's [row, col] source_tile to the EncodedCell frame the
// client provenance uses. Returns undefined on ANY malformation so a bad graph
// degrades to the per-slot union path (spec Phase 4) rather than throwing a
// player-facing error — the bar/loss gate (slotStates.ts) checks for an absent
// graph and falls back. Validation is intentionally strict: a partial or
// coordinate-out-of-range graph could mislead the walk (false orange →
// lost-winnable game, §4.1), so we reject rather than ship a half-trusted DAG.
//
// `raw` is typed `unknown` (not WireSolutionGraph) so callers can hand the
// loosely-typed JSON value straight through — every field is range/shape-checked
// below, so no caller-side narrowing or cast is required.
export function parseWireSolutionGraph(
  raw: unknown,
): SolutionGraph | undefined {
  if (raw === undefined) return undefined
  if (raw === null || typeof raw !== 'object') return undefined
  const wire = raw as Record<string, unknown>
  const root = wire['root']
  const rawNodes = wire['nodes']
  const rawEdges = wire['edges']
  if (
    !Number.isInteger(root) ||
    typeof root !== 'number' ||
    !Array.isArray(rawNodes) ||
    !Array.isArray(rawEdges)
  ) {
    return undefined
  }

  const nodes: SolutionGraphNode[] = []
  const nodeIds = new Set<number>()
  for (const n of rawNodes) {
    if (n === null || typeof n !== 'object') return undefined
    const node = n as Record<string, unknown>
    const id = node['id']
    const win = node['win']
    if (!Number.isInteger(id) || typeof id !== 'number') return undefined
    if (typeof win !== 'boolean') return undefined
    nodes.push({ id, win })
    nodeIds.add(id)
  }
  // Root must reference a declared node.
  if (!nodeIds.has(root)) return undefined

  const edges: SolutionGraphEdge[] = []
  // Edge-uniqueness guard (spec §7.2): from any node, (sourceCell, direction)
  // maps to at most one outgoing edge — the walk's correctness rests on it. A
  // duplicate key would make the walk ambiguous, so reject the whole graph.
  const seenEdgeKeys = new Set<string>()
  for (const e of rawEdges) {
    if (e === null || typeof e !== 'object') return undefined
    const edge = e as Record<string, unknown>
    const from = edge['from']
    const to = edge['to']
    const direction = edge['direction']
    const tile = edge['source_tile']
    if (!Number.isInteger(from) || typeof from !== 'number') return undefined
    if (!Number.isInteger(to) || typeof to !== 'number') return undefined
    if (
      !Number.isInteger(direction) ||
      typeof direction !== 'number' ||
      direction < 0 ||
      direction > 3
    ) {
      return undefined
    }
    if (!Array.isArray(tile) || tile.length !== 2) return undefined
    const row = tile[0]
    const col = tile[1]
    if (!Number.isInteger(row) || typeof row !== 'number') return undefined
    if (!Number.isInteger(col) || typeof col !== 'number') return undefined
    if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
      return undefined
    }
    // Both endpoints must reference declared nodes.
    if (!nodeIds.has(from) || !nodeIds.has(to)) return undefined
    const sourceCell = encodeSourceTile(row, col)
    const key = `${String(from)}:${String(sourceCell)}:${String(direction)}`
    if (seenEdgeKeys.has(key)) return undefined
    seenEdgeKeys.add(key)
    edges.push({ from, sourceCell, direction, to })
  }

  return { root, nodes, edges }
}

// Replay the full history from `root` and classify every filled slot. The walk
// is a pure function of (graph, targetWord, exitedLetters, provenance). It is
// recomputed on every call — nothing persists.
//
// `exitedLetters` and `provenance` are length-parallel (slot i is the i-th exit
// produced by the i-th move). The walk only considers the first
// min(exitedLetters.length, provenance.length) slots.
export function walkSolutionGraph(
  graph: SolutionGraph,
  targetWord: string,
  exitedLetters: readonly string[],
  provenance: readonly SolutionGraphMove[],
): SolutionGraphWalkResult {
  // Adjacency: from → [edges]. A node may have multiple outgoing edges sharing
  // `from` (energetic root), so this is a list-per-from map, never one-per-from.
  const adjacency = new Map<number, SolutionGraphEdge[]>()
  for (const edge of graph.edges) {
    const list = adjacency.get(edge.from)
    if (list === undefined) {
      adjacency.set(edge.from, [edge])
    } else {
      list.push(edge)
    }
  }
  const nodeWin = new Map<number, boolean>()
  for (const node of graph.nodes) {
    nodeWin.set(node.id, node.win)
  }
  const targetChars = targetWord.toLowerCase()

  const states: FilledSlotState[] = []
  let pointer = graph.root
  let offGraph = false

  const count = Math.min(exitedLetters.length, provenance.length)
  for (let i = 0; i < count; i += 1) {
    const letter = exitedLetters[i]
    const move = provenance[i]
    // Both narrowed by `i < count`, but TS doesn't know that under
    // noUncheckedIndexedAccess. A missing entry can't classify; treat as the
    // end of the walk (defensive — never hit in practice).
    if (letter === undefined || move === undefined) break

    const want = i < targetChars.length ? targetChars[i] : undefined

    // Step 1: wrong letter for this slot → 'incorrect', AND off-graph (§13-7).
    if (want === undefined || letter.toLowerCase() !== want) {
      states.push('incorrect')
      offGraph = true
      continue
    }

    // Already off-graph: every later right-letter slot is orange, never blue.
    if (offGraph) {
      states.push('correct-alt')
      continue
    }

    // Step 2: look for an outgoing edge matching (sourceCell, direction).
    const edge = matchingEdge(adjacency, pointer, move)
    if (edge !== undefined) {
      states.push('correct-canonical')
      pointer = edge.to
    } else {
      states.push('correct-alt')
      offGraph = true
    }
  }

  const onWinNode = !offGraph && (nodeWin.get(pointer) ?? false)
  return { slotStates: states, offGraph, onWinNode }
}

function matchingEdge(
  adjacency: ReadonlyMap<number, SolutionGraphEdge[]>,
  node: number,
  move: SolutionGraphMove,
): SolutionGraphEdge | undefined {
  const edges = adjacency.get(node)
  if (edges === undefined) return undefined
  return edges.find(
    (e) => e.sourceCell === move.sourceCell && e.direction === move.direction,
  )
}
